'use client';

import React, { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Button } from '@/components/ui/button';
import BorderWrapper from '@/components/ui/border-wrapper';
import Link from 'next/link';
import AnimatedCard from '@/components/ui/animated-card';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

const shapeVariants = {
  visible: {
    scale: [0.9, 1, 0.9] as number[],
    transition: { duration: 5, ease: 'easeInOut', repeat: Infinity },
  },
} as const;

const OurWork = () => {
  const t = useTranslations("ourWork");
    const sectionRef = useRef<HTMLDivElement>(null);
    const isInView = useInView(sectionRef, { once: true, margin: "0px 0px -60px 0px" });

    const cs = useTranslations("caseStudies");
    const carouselItems = [
        {
            title: cs('taksverden.card.title'),
            description: cs('taksverden.card.description'),
            image: '/assets/case-studies/taksverden/featured.webp',
            tags: [cs('taksverden.card.tags.0'), cs('taksverden.card.tags.1'), cs('taksverden.card.tags.2')],
            button: {
                text: t('viewCaseStudy'),
                href: '/case-study/taksverden',
            }
        },
        {
            title: cs('iti.card.title'),
            description: cs('iti.card.description'),
            image: '/assets/case-studies/iti/featured.webp',
            tags: [cs('iti.card.tags.0'), cs('iti.card.tags.1'), cs('iti.card.tags.2')],
            button: {
                text: t('viewCaseStudy'),
                href: '/case-study/iti',
            }
        },
    ]
return (
    <section className="section pt-[40px] pb-[30px] lg:pb-[40px] overflow-hidden" id='our-work'>
      <motion.div
        ref={sectionRef}
        className="container"
        initial="hidden"
        animate={isInView ? "show" : "hidden"}
        variants={{ hidden: { opacity: 1 }, show: { opacity: 1, transition: { staggerChildren: 0.12 } } }}
      >
        <motion.div
          className="section-header mb-[30px]"
          variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } }}
        >
            <h2 className="text-[28px] md:text-[40px] lg:text-[60px] font-semibold leading-[1.5]">
              {t("title")}
            </h2>
        </motion.div>

        {/* CAROUSEL */}
        <motion.div
          className="relative"
          variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } }}
        >
          <Carousel
            aria-label={t("title")}
            opts={{
              align: "center",
              loop: false,
              watchDrag: carouselItems.length > 1,
            }}
            className="w-full [&>div]:!overflow-visible"
          >
            <CarouselContent className="-ml-2 md:-ml-[32px]">
              { carouselItems.length > 0 && carouselItems.map((item, index) => (
                  
              <CarouselItem key={index} className="pl-2 md:pl-[32px] basis-1/1 md:basis-3/4">
                <AnimatedCard
                    className='rounded-[20px_0_20px_0] lg:rounded-[25px] h-full'
                >
                  <BorderWrapper
                      wrapperClass='rounded-[20px_0_20px_0] lg:rounded-[25px] h-full'
                      className='rounded-[20px_0_20px_0] lg:rounded-[25px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] md:pl-[60px] pt-[20px] md:pt-[40px] overflow-hidden relative flex flex-col'
                  >
                      <div
                        className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                        />
                      <div className="pl-[20px] md:pl-0 mb-[30px]">
                          { item.title && (
                            <h3 className="text-[24px] lg:text-[36px] font-semibold leading-[1.3] mb-[15px]">{item.title}</h3>
                          )}
                          { item.tags && item.tags.length > 0 && (
                            <div className="flex flex-wrap gap-[10px] mb-[20px]">
                              { item.tags.map((tag, index) => (
                                <span key={index} className="text-white px-[20px] font-light py-[5px] lg:py-[8px] bg-[#1D1C36] rounded-full text-[12px] lg:text-[16px] leading-[1.5]">{tag}</span>
                              ))}
                            </div>
                          )}
                          { item.button && (
                            <Button variant={'theme'} className='text-white font-light px-[20px] py-[10px] lg:px-[35px] lg:py-[15px] rounded-[10px] h-[40px] lg:h-[50px] text-[12px] lg:text-[16px] leading-[1.5]' asChild>
                              <Link href={item.button.href} aria-label={`${t("viewCaseStudy")} — ${item.title}`}>{t("viewCaseStudy")}</Link>
                            </Button>
                          )}
                      </div>

                      {/* IMAGE */}
                      <Link href={item.button.href} className="mt-auto">
                        <Image src={item.image} alt={item.title} width={1482} height={742} quality={60} sizes="(min-width: 1024px) 50vw, 85vw" className='w-full overflow-hidden rounded-[25px_0_25px_0] object-cover h-[260px] lg:h-[370px] hover:opacity-90 transition-opacity duration-300' />
                      </Link>
                  </BorderWrapper>
                </AnimatedCard>
              </CarouselItem>
              ))}
            </CarouselContent>
            
            {/* NAVIGATION CONTROLS */}
            {carouselItems.length > 1 && (
              <div className="flex justify-center items-center mt-[30px] gap-[15px]">
                <CarouselPrevious className="static translate-x-0 translate-y-0 w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border-white/5 focus:bg-theme focus:text-white" />
                <CarouselNext className="static translate-x-0 translate-y-0 w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border-white/5 focus:bg-theme focus:text-white" />
              </div>
            )}  
          </Carousel>
        </motion.div>

      </motion.div>
    </section>
)
}

export default OurWork